import { useState, useEffect } from "react";
import { api } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, User, MapPin, Clock, Filter } from "lucide-react";
import { toast } from "sonner";

const UserActivity = () => {
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchEmail, setSearchEmail] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, usersRes] = await Promise.all([
        api.get("/admin/events?limit=50"),
        api.get("/admin/users?limit=100")
      ]);
      setEvents(eventsRes.data);
      setUsers(usersRes.data.users || usersRes.data);
    } catch (error) {
      toast.error("Failed to load activity data");
    } finally {
      setLoading(false);
    }
  };

  const getUserByEmail = (email) => {
    return users.find(u => u.email === email);
  };

  const filteredUsers = users.filter(user => {
    const matchesRole = filter === "all" || user.role === filter;
    const matchesSearch = !searchEmail || user.email.toLowerCase().includes(searchEmail.toLowerCase());
    return matchesRole && matchesSearch;
  });

  const getEventIcon = (type) => {
    switch(type) {
      case 'points_earned': return '💰';
      case 'redemption': return '🎁';
      case 'tier_upgrade': return '⬆️';
      case 'login': return '🔐';
      default: return '📌';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout title="User Activity">
        <div className="space-y-4">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="h-24 rounded-xl animate-shimmer" />
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="User Activity">
      <div className="space-y-6 animate-fade-in">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-[#131316] border-[#2A2A2F]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A0A0A0] text-sm">Total Users</p>
                  <p className="text-3xl font-bold text-white mt-1">{users.length}</p>
                </div>
                <User className="w-10 h-10 text-[#D4AF37] opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#131316] border-[#2A2A2F]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A0A0A0] text-sm">Customers</p>
                  <p className="text-3xl font-bold text-[#4ADE80] mt-1">
                    {users.filter(u => u.role === 'customer').length}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#4ADE80]/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#4ADE80]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#131316] border-[#2A2A2F]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A0A0A0] text-sm">Staff</p>
                  <p className="text-3xl font-bold text-[#60A5FA] mt-1">
                    {users.filter(u => u.role === 'staff').length}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#60A5FA]/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#60A5FA]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#131316] border-[#2A2A2F]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A0A0A0] text-sm">Recent Events</p>
                  <p className="text-3xl font-bold text-[#F59E0B] mt-1">{events.length}</p>
                </div>
                <Activity className="w-10 h-10 text-[#F59E0B] opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Filter size={20} className="text-[#D4AF37]" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[#A0A0A0] text-sm">Search by Email</label>
                <Input
                  placeholder="Enter email address..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[#A0A0A0] text-sm">Filter by Role</label>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="bg-[#1A1A1E] border-[#2A2A2F] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#131316] border-[#2A2A2F]">
                    <SelectItem value="all" className="text-white">All Users</SelectItem>
                    <SelectItem value="customer" className="text-white">Customers Only</SelectItem>
                    <SelectItem value="staff" className="text-white">Staff Only</SelectItem>
                    <SelectItem value="admin" className="text-white">Admins Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User List */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader>
            <CardTitle className="text-white">User List</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="bg-[#1A1A1E] border border-[#2A2A2F] rounded-lg p-4 hover:border-[#D4AF37] transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        user.role === 'admin' ? 'bg-[#F59E0B]/10 text-[#F59E0B]' :
                        user.role === 'staff' ? 'bg-[#60A5FA]/10 text-[#60A5FA]' :
                        'bg-[#4ADE80]/10 text-[#4ADE80]'
                      }`}>
                        <User size={20} />
                      </div>
                      <div>
                        <p className="text-white font-medium">{user.name}</p>
                        <p className="text-[#A0A0A0] text-sm">{user.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`badge ${
                        user.role === 'admin' ? 'badge-warning' :
                        user.role === 'staff' ? 'badge-info' :
                        'badge-success'
                      }`}>
                        {user.role}
                      </span>
                      <p className="text-[#6B6B6B] text-xs mt-1 flex items-center gap-1 justify-end">
                        <Clock size={12} />
                        {formatDate(user.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {filteredUsers.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-[#6B6B6B]">No users found matching your filters</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Activity size={20} className="text-[#D4AF37]" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {events.map((event, idx) => {
                const user = users.find(u => u.id === event.user_id);
                return (
                  <div
                    key={idx}
                    className="bg-[#1A1A1E] border border-[#2A2A2F] rounded-lg p-4"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{getEventIcon(event.type)}</span>
                      <div className="flex-1">
                        <p className="text-white font-medium">{event.description}</p>
                        <p className="text-[#A0A0A0] text-sm mt-1">
                          User: {user?.email || event.user_id}
                        </p>
                        <p className="text-[#6B6B6B] text-xs mt-1 flex items-center gap-1">
                          <Clock size={12} />
                          {formatDate(event.created_at)}
                        </p>
                      </div>
                      <span className="badge badge-gold text-xs">{event.type}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Info */}
        <Card className="bg-[#0A0A0B] border-[#2A2A2F]">
          <CardContent className="p-4">
            <p className="text-[#6B6B6B] text-sm">
              <strong>Note:</strong> This page shows all user registrations and recent activity events. 
              Login tracking can be enhanced by adding login events to the events collection. 
              IP addresses and geolocation data can be tracked by extending the backend authentication endpoints.
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default UserActivity;
